import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { StatusBadge, PassTypeBadge, formatDate, formatDateTime } from "./StatusBadge";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { getTodaySummary, getPassesByDate } from "../lib/api";
import { Calendar, Loader2, Search, CheckCircle2, XCircle, Clock, Printer, Eye } from "lucide-react";

const TodayPastPasses = ({ showPrint = false }) => {
  const [tab, setTab] = useState("today");
  const [todayPasses, setTodayPasses] = useState([]);
  const [pastPasses, setPastPasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pastDate, setPastDate] = useState("");
  const [pastLoading, setPastLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { loadToday(); }, []);

  const loadToday = async () => {
    try {
      const { data } = await getTodaySummary();
      setTodayPasses(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const loadPast = async () => {
    if (!pastDate) { toast.error("Select a date"); return; }
    setPastLoading(true);
    try {
      const { data } = await getPassesByDate(pastDate);
      setPastPasses(data);
    } catch { toast.error("Failed to load"); }
    finally { setPastLoading(false); }
  };

  const approved = (list) => list.filter(p => p.status === "approved" || p.status === "gate_verified");
  const rejected = (list) => list.filter(p => p.status?.startsWith("rejected"));
  const pending = (list) => list.filter(p => p.status?.startsWith("pending"));
  const closed = (list) => list.filter(p => p.status === "closed");

  const PassTable = ({ passes, title }) => (
    <div>
      {passes.length === 0 ? (
        <p className="text-center py-6 text-slate-400 text-sm">No {title?.toLowerCase()} passes found.</p>
      ) : (
        <div className="border rounded-lg overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 text-xs">
                <TableHead className="text-xs">Req No.</TableHead>
                <TableHead className="text-xs">Student</TableHead>
                <TableHead className="text-xs">Course/Yr</TableHead>
                <TableHead className="text-xs">Type</TableHead>
                <TableHead className="text-xs">Exit</TableHead>
                <TableHead className="text-xs">Status</TableHead>
                {showPrint && <TableHead className="text-xs">Action</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {passes.map((p) => (
                <TableRow key={p.id} className="text-xs">
                  <TableCell className="font-mono text-xs py-2">{p.request_number}</TableCell>
                  <TableCell className="py-2">
                    <p className="font-medium text-xs">{p.student_name}</p>
                  </TableCell>
                  <TableCell className="py-2 text-xs">{p.course_name}-Y{p.year}</TableCell>
                  <TableCell className="py-2"><PassTypeBadge type={p.pass_type} /></TableCell>
                  <TableCell className="py-2 text-xs whitespace-nowrap">{formatDate(p.exit_date)} {p.exit_time}</TableCell>
                  <TableCell className="py-2"><StatusBadge status={p.status} /></TableCell>
                  {showPrint && (
                    <TableCell className="py-2">
                      {(p.status === "approved" || p.status === "gate_verified" || p.status === "closed") && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2"
                          onClick={() => navigate(`/print/${p.id}`)}
                          data-testid={`reprint-${p.id}`}
                          title={p.status === "closed" ? "View Pass" : "Print Pass"}
                        >
                          {p.status === "closed" ? <Eye className="h-3 w-3" /> : <Printer className="h-3 w-3" />}
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );

  const SummaryRow = ({ list, label }) => (
    <div className="flex gap-2 text-xs flex-wrap">
      <Badge variant="outline" className="text-xs">{label}: {list.length}</Badge>
      <Badge className="bg-emerald-100 text-emerald-700 text-xs">{approved(list).length} Approved</Badge>
      <Badge className="bg-red-100 text-red-700 text-xs">{rejected(list).length} Rejected</Badge>
      <Badge className="bg-amber-100 text-amber-700 text-xs">{pending(list).length} Pending</Badge>
      <Badge className="bg-slate-100 text-slate-700 text-xs">{closed(list).length} Closed</Badge>
    </div>
  );

  return (
    <Card className="border-slate-200" data-testid="today-past-passes">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Gate Pass Activity</CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="h-8">
            <TabsTrigger value="today" className="text-xs px-3 h-7" data-testid="today-tab">
              <Clock className="h-3 w-3 mr-1" /> Today
            </TabsTrigger>
            <TabsTrigger value="past" className="text-xs px-3 h-7" data-testid="past-tab">
              <Calendar className="h-3 w-3 mr-1" /> Past Date
            </TabsTrigger>
          </TabsList>

          <TabsContent value="today" className="mt-3 space-y-3">
            {loading ? (
              <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
            ) : (
              <>
                <SummaryRow list={todayPasses} label="Today" />
                <PassTable passes={todayPasses} title="today's" />
              </>
            )}
          </TabsContent>

          <TabsContent value="past" className="mt-3 space-y-3">
            <div className="flex items-end gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Select Date</Label>
                <Input
                  type="date"
                  value={pastDate}
                  max={new Date().toISOString().split("T")[0]}
                  onChange={(e) => setPastDate(e.target.value)}
                  className="w-40 h-8 text-xs"
                  data-testid="past-date-picker"
                />
              </div>
              <Button size="sm" className="h-8 text-xs" onClick={loadPast} disabled={pastLoading} data-testid="load-past-btn">
                {pastLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Search className="h-3 w-3 mr-1" />}
                View
              </Button>
            </div>
            {pastPasses.length > 0 && <SummaryRow list={pastPasses} label={formatDate(pastDate)} />}
            <PassTable passes={pastPasses} title="past" />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default TodayPastPasses;
